import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Nfc, Download, QrCode } from "lucide-react";
import { http, formatApiError, askDelete } from "../../lib/api";
import { hasNfc } from "../agent/CodeScanner";
import { SearchSelect } from "../SearchSelect";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inp = "border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";
const EMPTY = { name: "", instructions: "", nfc_uid: "" };

function PointForm({ siteId, edit, onSaved, onCancel }) {
  const [f, setF] = useState(edit || EMPTY);
  const [reading, setReading] = useState(false);
  const readTag = async () => {
    try {
      const r = new window.NDEFReader(); const c = new AbortController();
      setReading(true);
      await r.scan({ signal: c.signal });
      r.onreading = (e) => { setF((x) => ({ ...x, nfc_uid: e.serialNumber })); setReading(false); c.abort(); };
    } catch { setReading(false); toast.error("Lecture NFC impossible sur cet appareil"); }
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { site_id: siteId, name: f.name, instructions: f.instructions, nfc_uid: f.nfc_uid };
      if (edit) await http.put(`/rounds/points/${edit.id}`, body); else await http.post("/rounds/points", body);
      toast.success("Point enregistré"); setF(EMPTY); onSaved();
    } catch (er) { toast.error(formatApiError(er)); }
  };
  return (
    <form onSubmit={save} className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="round-point-form">
      <label className="text-xs font-semibold text-slate-600">Nom du point<input data-testid="round-point-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} w-full mt-1`} placeholder="Ex. Porte quai 3" /></label>
      <label className="text-xs font-semibold text-slate-600">Consigne à afficher<input data-testid="round-point-instructions" value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} className={`${inp} w-full mt-1`} placeholder="Optionnel" /></label>
      <label className="text-xs font-semibold text-slate-600">Numéro du badge NFC
        <div className="flex gap-1 mt-1">
          <input data-testid="round-point-nfc" value={f.nfc_uid} onChange={(e) => setF({ ...f, nfc_uid: e.target.value })} className={`${inp} w-full font-mono`} placeholder="Ex. 04:A2:3B:1C:6F:80" />
          {hasNfc() && <button type="button" data-testid="round-point-read-nfc" onClick={readTag} className={`${inp} shrink-0`} title="Lire le badge"><Nfc className={`h-4 w-4 ${reading ? "text-emerald-600 animate-pulse" : ""}`} /></button>}
        </div>
      </label>
      <div className="flex gap-1">
        <button data-testid="round-point-save" className="bg-[#0B2A7A] text-white text-sm font-semibold rounded-md px-4 py-2">{edit ? "Enregistrer" : "Ajouter"}</button>
        {edit && <button type="button" data-testid="round-point-cancel" onClick={onCancel} className="border border-slate-300 text-sm rounded-md px-3 py-2">Annuler</button>}
      </div>
    </form>
  );
}

export function RoundPointsAdmin() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [points, setPoints] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => { http.get("/sites").then((r) => { setSites(r.data); if (r.data[0]) setSiteId(r.data[0].id); }); }, []);
  const load = () => siteId && http.get("/rounds/points", { params: { site_id: siteId } }).then((r) => setPoints(r.data));
  useEffect(() => { load(); setEdit(null); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (p) => {
    if (!askDelete(`Supprimer le point « ${p.name} » ?`)) return;
    try { await http.delete(`/rounds/points/${p.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="round-points-admin">
      <div className="flex flex-wrap items-center gap-2">
        <SearchSelect data-testid="round-points-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inp}>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SearchSelect>
        {points.length > 0 && <a data-testid="round-points-labels" href={`${BACKEND}/api/rounds/labels.pdf?site_id=${siteId}`} className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 rounded-md px-3 py-2 text-sm font-semibold"><Download className="h-4 w-4" />Étiquettes QR code (PDF)</a>}
      </div>
      {siteId && <PointForm key={edit?.id || "new"} siteId={siteId} edit={edit} onSaved={() => { setEdit(null); load(); }} onCancel={() => setEdit(null)} />}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="round-points-list-admin">
        {points.length === 0 && <div className="p-5 text-sm text-slate-500 text-center">Aucun point de ronde sur ce site.</div>}
        {points.map((p) => (
          <div key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`round-point-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{p.name}</div>
              <div className="text-xs text-slate-500 truncate">{p.instructions || "Sans consigne"}</div>
            </div>
            <span className="text-xs inline-flex items-center gap-1 text-slate-600"><Nfc className="h-3.5 w-3.5" />{p.nfc_uid || <em className="text-slate-400">pas de badge</em>}</span>
            <span className="text-xs inline-flex items-center gap-1 text-slate-600"><QrCode className="h-3.5 w-3.5" />QR</span>
            <button data-testid={`round-point-edit-${p.id}`} onClick={() => setEdit(p)} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`round-point-delete-${p.id}`} onClick={() => del(p)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Plus className="h-3 w-3" />Chaque point a son QR code (étiquette à imprimer). Le badge NFC se lit avec un téléphone Android ; sur iPhone, l&apos;agent scanne le QR code.</p>
    </div>
  );
}
